import {
  Card,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Fab,
  Grid,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { Add, ReceiptLong } from "@mui/icons-material";
import { useEffect, useMemo, useState } from "react";
import { useTheme } from "@mui/material/styles";

import colors from "assets/theme/base/colors";
import rgba from "assets/theme/functions/rgba";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import DataTable from "examples/Tables/DataTable";
import FormField from "layouts/applications/wizard/components/FormField";
import ApiCalls from "services/flick_erp_api_call";

const blankExtra = {
  contract: "",
  line_type: "allowance",
  name: "",
  amount: "",
  start_date: "",
  end_date: "",
  bill: null,
};

const statusColor = {
  pending: "warning",
  approved: "success",
  rejected: "error",
  cancelled: "default",
};

const contractLabel = (contract) => {
  const user = contract?.employee?.user || {};
  const employeeName = user.full_name || `${user.first_name || ""} ${user.last_name || ""}`.trim() || user.email;
  return employeeName || contract?.bank?.name_in_account || `Contract #${contract?.id}`;
};

export function DirectPayrollExtras({ permissions = [], modulePermissions = {} }) {
  const theme = useTheme();
  const palette = theme.palette;
  const darkMode = false;
  const [extras, setExtras] = useState({ columns: [], rows: [] });
  const [contracts, setContracts] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [payload, setPayload] = useState(blankExtra);

  const approvePermission = modulePermissions?.approve_permission || "payrole | expense or incentive or deduction|approve";
  const updatePermission = modulePermissions?.update_permission || "payrole | expense or incentive or deduction|update";
  const canManage = permissions?.includes(approvePermission) || permissions?.includes(updatePermission);

  const dialogPaperSx = {
    backgroundColor: darkMode ? palette.background.card : colors.white.main,
    boxShadow: darkMode ? "0 24px 48px rgba(2, 6, 23, 0.42)" : "none",
    width: "auto",
    maxWidth: "none",
    borderRadius: "20px",
    border: `1px solid ${darkMode ? rgba(palette.white.main, 0.08) : rgba(colors.secondary.main, 0.14)}`,
  };

  const buildFormData = () => {
    const formData = new FormData();
    ["contract", "line_type", "name", "amount", "start_date", "end_date"].forEach((field) => {
      formData.append(field, payload[field] || "");
    });
    if (payload.bill instanceof File) formData.append("bill", payload.bill);
    return formData;
  };

  async function getContracts() {
    const res = await ApiCalls.getEmployeeContract("?is_active=true");
    if (res.success && res.code === 200) setContracts(res.data || []);
  }

  async function getExtras() {
    const res = await ApiCalls.getExpenseIncentiveDeductions();
    if (res.success && res.code === 200) setExtras((state) => ({ ...state, rows: res.data || [] }));
  }

  async function createExtra() {
    const res = await ApiCalls.createExpenseIncentiveDeductions(buildFormData(), true);
    if (res.success) {
      setPayload(blankExtra);
      setDialogOpen(false);
      getExtras();
    }
  }

  const columns = useMemo(
    () => [
      { Header: "EMPLOYEE", accessor: "employee_name" },
      { Header: "NAME", accessor: "name" },
      { Header: "TYPE", accessor: "line_type" },
      { Header: "AMOUNT", accessor: "amount" },
      { Header: "START DATE", accessor: "start_date" },
      { Header: "END DATE", accessor: "end_date" },
      {
        Header: "STATUS",
        accessor: "status",
        Cell: ({ row }) => (
          <Stack direction="row" spacing={1}>
            <Chip size="small" color={statusColor[row.original.status] || "default"} label={row.original.status_display || row.original.status} />
            {row.original.payroll_batch_label && <Chip size="small" color="info" variant="outlined" label={`Payroll ${row.original.payroll_batch_label}`} />}
          </Stack>
        ),
      },
      { Header: "REQUESTED BY", accessor: "requested_by_name" },
      { Header: "APPROVED BY", accessor: "approved_by_name" },
      {
        Header: "BILL",
        accessor: "bill",
        Cell: ({ row }) =>
          row.original.bill ? (
            <Tooltip title="Open bill">
              <IconButton size="small" component="a" href={row.original.bill} target="_blank" rel="noreferrer">
                <ReceiptLong fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : (
            <MDTypography variant="caption" color="text">-</MDTypography>
          ),
      },
    ],
    []
  );

  useEffect(() => {
    getExtras();
    getContracts();
  }, []);

  useEffect(() => {
    setExtras((state) => ({ ...state, columns }));
  }, [columns]);

  return (
    <>
      <Card>
        <MDBox pt={3} pl={3} pb={4} pr={3} display="flex" justifyContent="space-between" alignItems="center">
          <MDBox>
            <MDTypography variant="h4">HR Payroll Extras</MDTypography>
            <MDTypography variant="button" color="text">
              Direct payroll allowances or deductions created by HR/payroll approvers.
            </MDTypography>
          </MDBox>
          {canManage && (
            <Fab color="info" onClick={() => setDialogOpen(true)}>
              <Add sx={{ color: colors.white.main }} />
            </Fab>
          )}
        </MDBox>
        <MDBox>
          <DataTable table={extras} entriesPerPage={{ defaultValue: 100, entries: [5, 10, 15, 20, 25, 30] }} showTotalEntries isSorted={false} />
        </MDBox>
      </Card>

      <Dialog PaperProps={{ sx: dialogPaperSx }} open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle sx={{ width: "100%", p: "20px", background: `linear-gradient(135deg, ${colors.info.main} 0%, ${colors.info.focus} 100%)`, color: "white" }}>
          <Typography sx={{ color: "white", fontWeight: "Medium" }}>Create Direct Payroll Extra</Typography>
        </DialogTitle>
        <DialogContent sx={{ p: "4px", backgroundColor: colors.white.main }}>
          <MDBox p={3} lineHeight={1}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormField select SelectProps={{ native: true }} label="Employee Contract" value={payload.contract} onChange={(e) => setPayload((state) => ({ ...state, contract: e.target.value }))}>
                  <option value="">Select employee contract</option>
                  {contracts.map((contract) => <option key={contract.id} value={contract.id}>{contractLabel(contract)}</option>)}
                </FormField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormField select SelectProps={{ native: true }} label="Line Type" value={payload.line_type} onChange={(e) => setPayload((state) => ({ ...state, line_type: e.target.value }))}>
                  <option value="allowance">Allowance</option>
                  <option value="deduction">Deduction</option>
                </FormField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormField label="Name" value={payload.name} onChange={(e) => setPayload((state) => ({ ...state, name: e.target.value }))} placeholder="Extra working day, travel reimbursement..." />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormField label="Amount" value={payload.amount} inputProps={{ type: "number" }} onChange={(e) => setPayload((state) => ({ ...state, amount: e.target.value }))} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <MDButton variant="outlined" color="info" component="label" fullWidth sx={{ height: "100%" }}>
                  {payload.bill instanceof File ? payload.bill.name : "Upload Bill / Proof"}
                  <input hidden type="file" accept="image/*,.pdf" onChange={(e) => setPayload((state) => ({ ...state, bill: e.target.files?.[0] || null }))} />
                </MDButton>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormField label="Start Date" InputLabelProps={{ shrink: true }} value={payload.start_date} inputProps={{ type: "date" }} onChange={(e) => setPayload((state) => ({ ...state, start_date: e.target.value }))} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormField label="End Date" InputLabelProps={{ shrink: true }} value={payload.end_date} inputProps={{ type: "date" }} onChange={(e) => setPayload((state) => ({ ...state, end_date: e.target.value }))} />
              </Grid>
            </Grid>
            <MDBox pt={3} display="flex" justifyContent="flex-end">
              <MDButton variant="gradient" color="dark" disabled={!payload.contract} onClick={createExtra}>Create Approved Extra</MDButton>
            </MDBox>
          </MDBox>
        </DialogContent>
      </Dialog>
    </>
  );
}
